import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Plus, Trash2, ShieldAlert, Users, ChevronDown, ChevronRight } from "lucide-react";
import { AssignmentChecklist } from "@/components/AssignmentChecklist";
import { CompanyUsersAdmin } from "@/components/CompanyUsersAdmin";

export const Route = createFileRoute("/coach-bolag")({
  head: () => ({
    meta: [
      { title: "Coach-tilldelning — Inkubera" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CoachAssignPage,
});

type Company = { id: string; name: string };
type Coach = { id: string; full_name: string; email: string; active: boolean };
type Assignment = {
  id: string;
  company_id: string;
  coach_id: string;
  coach_user_id: string | null;
  is_primary: boolean;
};
type ChecklistRow = {
  assignment_id: string;
  nda_completed_at: string | null;
  coach_agreement_completed_at: string | null;
  framework_confirmed_at: string | null;
  intro_meeting_completed_at: string | null;
};

const CHECK_FIELDS = [
  "nda_completed_at",
  "coach_agreement_completed_at",
  "framework_confirmed_at",
  "intro_meeting_completed_at",
] as const;

function maturity(cl: ChecklistRow | undefined) {
  const done = cl ? CHECK_FIELDS.filter((f) => cl[f]).length : 0;
  const total = CHECK_FIELDS.length;
  const pct = Math.round((done / total) * 100);
  let label = "Ej startad";
  let cls = "bg-muted text-muted-foreground";
  if (done === total) {
    label = "Klar";
    cls = "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30";
  } else if (done >= 2) {
    label = "Pågår";
    cls = "bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30";
  } else if (done >= 1) {
    label = "Påbörjad";
    cls = "bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30";
  } else {
    cls = "bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/30";
  }
  return { done, total, pct, label, cls };
}

function CoachAssignPage() {
  return (
    <AppShell>
      <Inner />
    </AppShell>
  );
}

function Inner() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [checklists, setChecklists] = useState<Record<string, ChecklistRow>>({});
  const [loading, setLoading] = useState(true);
  const [pickerCompany, setPickerCompany] = useState<string | null>(null);
  const [pickerCoach, setPickerCoach] = useState<string>("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) =>
    setExpanded((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const load = async () => {
    setLoading(true);
    if (user) {
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      setIsAdmin((roles ?? []).some((r) => r.role === "admin"));
    }
    const [{ data: comps }, { data: chs }, { data: asgn }, { data: cls }] = await Promise.all([
      supabase.from("companies").select("id, name").order("name"),
      supabase.from("coaches").select("id, full_name, email, active").order("full_name"),
      supabase.from("company_coaches").select("*"),
      supabase
        .from("assignment_checklist")
        .select("assignment_id, nda_completed_at, coach_agreement_completed_at, framework_confirmed_at, intro_meeting_completed_at"),
    ]);
    setCompanies((comps ?? []) as Company[]);
    setCoaches((chs ?? []) as Coach[]);
    setAssignments((asgn ?? []) as Assignment[]);
    const map: Record<string, ChecklistRow> = {};
    for (const r of (cls ?? []) as ChecklistRow[]) map[r.assignment_id] = r;
    setChecklists(map);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [user]);

  const assign = async (companyId: string, coachId: string) => {
    if (!coachId) return;
    const coach = coaches.find((c) => c.id === coachId);
    // Försök matcha coach.email mot profil-användare
    const { data: prof } = await supabase
      .from("profiles")
      .select("id")
      .eq("email", coach?.email ?? "")
      .maybeSingle();
    const { error } = await supabase.from("company_coaches").insert({
      company_id: companyId,
      coach_id: coachId,
      coach_user_id: prof?.id ?? null,
      assigned_by: user?.id ?? null,
    });
    if (error) {
      alert(error.message);
      return;
    }
    setPickerCompany(null);
    setPickerCoach("");
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Ta bort coach-tilldelning?")) return;
    const { error } = await supabase.from("company_coaches").delete().eq("id", id);
    if (error) alert(error.message);
    else load();
  };

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Laddar…</div>;
  if (!isAdmin) {
    return (
      <div className="p-8 max-w-xl">
        <div className="rounded-2xl border border-border bg-card p-8 flex items-start gap-4">
          <ShieldAlert className="size-6 text-destructive shrink-0" />
          <div>
            <h1 className="font-display text-xl">Endast administratörer</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Coach-tilldelning kräver administratörsbehörighet.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl space-y-6">
      <header>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">
          <Users className="size-3.5" /> Tilldelning
        </div>
        <h1 className="font-display text-3xl">Coacher per bolag</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Tilldela en eller flera affärscoacher till varje bolag. Tilldelade coacher får
          månatliga påminnelser om månadsrapport och TRL-utvärdering.
        </p>
      </header>

      {companies.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Inga bolag i portföljen ännu. När prospect godkänns skapas bolag automatiskt.
        </div>
      )}

      <div className="space-y-3">
        {companies.map((c) => {
          const ass = assignments.filter((a) => a.company_id === c.id);
          return (
            <div key={c.id} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="font-medium">{c.name}</div>
                <button
                  onClick={() => setPickerCompany(pickerCompany === c.id ? null : c.id)}
                  className="text-xs px-3 py-1.5 rounded-md bg-secondary hover:bg-secondary/80 inline-flex items-center gap-1.5"
                >
                  <Plus className="size-3" /> Lägg till coach
                </button>
              </div>

              {pickerCompany === c.id && (
                <div className="mt-3 flex gap-2">
                  <select
                    value={pickerCoach}
                    onChange={(e) => setPickerCoach(e.target.value)}
                    className="flex-1 h-9 px-3 rounded-lg bg-secondary text-sm"
                  >
                    <option value="">— välj coach —</option>
                    {coaches
                      .filter((co) => co.active && !ass.find((a) => a.coach_id === co.id))
                      .map((co) => (
                        <option key={co.id} value={co.id}>
                          {co.full_name} ({co.email})
                        </option>
                      ))}
                  </select>
                  <button
                    onClick={() => assign(c.id, pickerCoach)}
                    disabled={!pickerCoach}
                    className="h-9 px-3 rounded-lg bg-foreground text-background text-sm font-medium disabled:opacity-50"
                  >
                    Tilldela
                  </button>
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                {ass.length === 0 && (
                  <span className="text-xs text-muted-foreground">Ingen coach tilldelad</span>
                )}
                {ass.map((a) => {
                  const coach = coaches.find((co) => co.id === a.coach_id);
                  const isOpen = expanded.has(a.id);
                  const m = maturity(checklists[a.id]);
                  return (
                    <div key={a.id} className="w-full">
                      <div className="flex items-center gap-2 flex-wrap rounded-full bg-secondary px-3 py-1.5 text-xs w-fit max-w-full">
                        <button
                          onClick={() => toggleExpanded(a.id)}
                          className="inline-flex items-center gap-1.5 hover:opacity-80"
                        >
                          {isOpen ? (
                            <ChevronDown className="size-3" />
                          ) : (
                            <ChevronRight className="size-3" />
                          )}
                          <span>{coach?.full_name ?? "Okänd coach"}</span>
                        </button>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${m.cls}`}
                          title={`${m.done}/${m.total} avtalspunkter klara`}
                        >
                          {m.label} · {m.done}/{m.total}
                        </span>
                        <div className="hidden sm:block w-16 h-1 rounded-full bg-background/60 overflow-hidden">
                          <div
                            className={
                              m.done === m.total
                                ? "h-full bg-emerald-500"
                                : m.done >= 2
                                ? "h-full bg-amber-500"
                                : m.done >= 1
                                ? "h-full bg-blue-500"
                                : "h-full bg-rose-500"
                            }
                            style={{ width: `${m.pct}%` }}
                          />
                        </div>
                        {!a.coach_user_id && (
                          <span
                            className="text-amber-600 dark:text-amber-400"
                            title="Coachen har inget användarkonto än — påminnelse kan inte skickas"
                          >
                            ⚠
                          </span>
                        )}
                        <button
                          onClick={() => remove(a.id)}
                          className="text-muted-foreground hover:text-destructive ml-auto"
                        >
                          <Trash2 className="size-3" />
                        </button>
                      </div>
                      {isOpen && <AssignmentChecklist assignmentId={a.id} onChange={load} />}
                    </div>
                  );
                })}
              </div>

              <CompanyUsersAdmin companyId={c.id} />
            </div>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground">
        ⚠ visas när coachens e-postadress inte matchar något användarkonto. Bjud in coachen
        via <em>Användare</em> först så att påminnelser kan kopplas till rätt person.
      </p>
    </div>
  );
}
